import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { LayoutStyle, BuiltinLayoutStyleId } from '../../types/workspace.types';
import { LayoutState, TabContainer, SplitPane } from '../../types/pane.types';
import { createLayoutStyleId, createPaneId, createTabId } from '../../types';

interface LayoutStyleStore {
  // State
  styles: LayoutStyle[];

  // Actions
  initializeStyles: () => void;
  createStyle: (name: string, description: string | undefined, layoutState: LayoutState) => string;
  deleteStyle: (styleId: string) => void;
  getStyleById: (styleId: string) => LayoutStyle | null;
  getAvailableStyles: () => LayoutStyle[];
}

// ビルトインスタイルの定義
const builtinStyles: LayoutStyle[] = [
  {
    id: createLayoutStyleId(BuiltinLayoutStyleId.DEFAULT),
    name: 'Default',
    description: 'Single pane layout',
    isBuiltin: true,
    layoutState: {
      rootPane: {
        type: 'container',
        id: createPaneId('default-container'),
        tabs: [],
        activeTabId: createTabId(''),
      } as TabContainer,
      activeContainerId: createPaneId('default-container'),
    },
  },
  {
    id: createLayoutStyleId(BuiltinLayoutStyleId.THREE_PANE_ANALYSIS),
    name: 'Three Pane Analysis',
    description: 'Data table on left, analysis on top right, details on bottom right',
    isBuiltin: true,
    layoutState: {
      rootPane: {
        type: 'split',
        id: createPaneId('root-split'),
        direction: 'horizontal',
        splitRatio: 0.5,
        leftChild: {
          type: 'container',
          id: createPaneId('left-container'),
          tabs: [],
          activeTabId: createTabId(''),
        } as TabContainer,
        rightChild: {
          type: 'split',
          id: createPaneId('right-split'),
          direction: 'vertical',
          splitRatio: 0.6,
          leftChild: {
            type: 'container',
            id: createPaneId('top-right-container'),
            tabs: [],
            activeTabId: createTabId(''),
          } as TabContainer,
          rightChild: {
            type: 'container',
            id: createPaneId('bottom-right-container'),
            tabs: [],
            activeTabId: createTabId(''),
          } as TabContainer,
        } as SplitPane,
      } as SplitPane,
      activeContainerId: createPaneId('left-container'),
    },
  },
  {
    id: createLayoutStyleId(BuiltinLayoutStyleId.HORIZONTAL_SPLIT),
    name: 'Horizontal Split',
    description: 'Two panes side by side',
    isBuiltin: true,
    layoutState: {
      rootPane: {
        type: 'split',
        id: createPaneId('root-split'),
        direction: 'horizontal',
        splitRatio: 0.5,
        leftChild: {
          type: 'container',
          id: createPaneId('left-container'),
          tabs: [],
          activeTabId: createTabId(''),
        } as TabContainer,
        rightChild: {
          type: 'container',
          id: createPaneId('right-container'),
          tabs: [],
          activeTabId: createTabId(''),
        } as TabContainer,
      } as SplitPane,
      activeContainerId: createPaneId('left-container'),
    },
  },
  {
    id: createLayoutStyleId(BuiltinLayoutStyleId.VERTICAL_SPLIT),
    name: 'Vertical Split',
    description: 'Two panes stacked vertically',
    isBuiltin: true,
    layoutState: {
      rootPane: {
        type: 'split',
        id: createPaneId('root-split'),
        direction: 'vertical',
        splitRatio: 0.5,
        leftChild: {
          type: 'container',
          id: createPaneId('top-container'),
          tabs: [],
          activeTabId: createTabId(''),
        } as TabContainer,
        rightChild: {
          type: 'container',
          id: createPaneId('bottom-container'),
          tabs: [],
          activeTabId: createTabId(''),
        } as TabContainer,
      } as SplitPane,
      activeContainerId: createPaneId('top-container'),
    },
  },
  {
    id: createLayoutStyleId(BuiltinLayoutStyleId.RESIDUAL_ANALYSIS),
    name: 'Residual Analysis',
    description: 'Data table on left, residual analysis on top right, selected rows on bottom right',
    isBuiltin: true,
    layoutState: {
      rootPane: {
        type: 'split',
        id: createPaneId('root-split'),
        direction: 'horizontal',
        splitRatio: 0.5,
        leftChild: {
          type: 'container',
          id: createPaneId('left-pane'),
          tabs: [],
          activeTabId: createTabId(''),
        } as TabContainer,
        rightChild: {
          type: 'split',
          id: createPaneId('right-split'),
          direction: 'vertical',
          splitRatio: 0.6,
          leftChild: {
            type: 'container',
            id: createPaneId('right-top-pane'),
            tabs: [],
            activeTabId: createTabId(''),
          } as TabContainer,
          rightChild: {
            type: 'container',
            id: createPaneId('right-bottom-pane'),
            tabs: [],
            activeTabId: createTabId(''),
          } as TabContainer,
        } as SplitPane,
      } as SplitPane,
      activeContainerId: createPaneId('left-pane'),
    },
    autoTabsConfig: {
      requiresContext: true,
      createTabs: (context) => {
        if (!context?.datasetId) return [];
        
        return [
          {
            paneId: createPaneId('left-pane'),
            tab: {
              type: 'data-table',
              title: `Dataset ${context.datasetName || context.datasetId}`,
              isClosable: true,
              keepMounted: true,
              metadata: { datasetId: context.datasetId }
            }
          },
          {
            paneId: createPaneId('right-bottom-pane'),
            tab: {
              type: 'selected-rows',
              title: 'Selected Rows',
              isClosable: true,
              keepMounted: true,
              metadata: { datasetId: context.datasetId }
            }
          }
        ];
      }
    },
  },
];

export const useLayoutStyleStore = create<LayoutStyleStore>()(
  devtools(
    immer((set, get) => ({
      // State
      styles: [],

      // Actions
      initializeStyles: () => {
        set((state) => {
          // ビルトインスタイルで初期化
          state.styles = [...builtinStyles];
        });
      },

      createStyle: (name, description, layoutState) => {
        const newId = createLayoutStyleId(`layout-${crypto.randomUUID()}`);
        const newStyle: LayoutStyle = {
          id: newId,
          name,
          description,
          layoutState,
          isBuiltin: false,
        };

        set((state) => {
          state.styles.push(newStyle);
        });

        return newId;
      },

      deleteStyle: (styleId) => {
        set((state) => {
          const index = state.styles.findIndex((s) => s.id === styleId);
          if (index !== -1 && !state.styles[index].isBuiltin) {
            state.styles.splice(index, 1);
          }
        });
      },

      getStyleById: (styleId) => {
        const { styles } = get();
        return styles.find((s) => s.id === styleId) || null;
      },

      getAvailableStyles: () => {
        return get().styles;
      },
    })),
    {
      name: 'layout-style-store',
    }
  )
);